import { EntityFormModal } from '@/components/EntityFormModal'
import { useSeededModalField } from '@/hooks/useSeededModalField'
import { evaluateIdentifierField, IDENTIFIER_RULE_MESSAGE } from '@/lib/identifierValidation'
import type { DocumentTypeEntry } from '@/types/bundle'
import { cloneForDuplicate, EMPTY_DOCUMENT_TYPE } from '../constants'

export interface NewOrDuplicateDocumentTypeModalProps {
  open: boolean
  onClose: () => void
  existingIds: string[]
  /** Present for 'duplicate', absent for '+ New' - drives title/prefill/duplicate logic. */
  source?: { id: string; entry: DocumentTypeEntry }
  onConfirm: (newId: string, initialEntry: DocumentTypeEntry) => void
}

// Both "+ New Document Type" and per-row "Duplicate" open this same form
// (empty vs. pre-filled), per Frontend PRD 03 AC7 - a document_type_id is
// mandatory either way since JSON object keys can't literally duplicate.
export function NewOrDuplicateDocumentTypeModal({
  open,
  onClose,
  existingIds,
  source,
  onConfirm,
}: NewOrDuplicateDocumentTypeModalProps) {
  const openKey = open ? (source?.id ?? '__new__') : null
  const { value: id, setValue: setId, reset } = useSeededModalField(openKey, () =>
    source ? `${source.id}_copy` : '',
  )

  const { trimmedId, isDuplicateId, isInvalidShape, isValid } = evaluateIdentifierField(id, existingIds)

  function handleClose() {
    reset()
    onClose()
  }

  function handleConfirm() {
    if (!isValid) return
    const initialEntry = source ? cloneForDuplicate(source.entry) : structuredClone(EMPTY_DOCUMENT_TYPE)
    onConfirm(trimmedId, initialEntry)
    handleClose()
  }

  return (
    <EntityFormModal
      open={open}
      entityLabel="Document Type"
      isDuplicate={Boolean(source)}
      isValid={isValid}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <div className="flex flex-col gap-1">
        <label className="text-meta font-medium text-text-secondary" htmlFor="new-document-type-id">
          Document type ID
        </label>
        <input
          id="new-document-type-id"
          type="text"
          autoFocus
          value={id}
          onChange={(event) => setId(event.target.value)}
          placeholder="e.g. memorandum_of_sale"
          className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
        {isDuplicateId && <span className="text-meta text-danger">A document type with this ID already exists</span>}
        {isInvalidShape && <span className="text-meta text-danger">{IDENTIFIER_RULE_MESSAGE}</span>}
      </div>
    </EntityFormModal>
  )
}
